import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { Router, type ActivatedRouteSnapshot } from '@angular/router';
import { Observable, catchError, defer, finalize, throwError } from 'rxjs';
import { shouldAutoReportHttpError } from '@dash/randy/shared/errors/error-policy';
import { ErrorReporterService } from '@dash/randy/shared/errors/error-reporter.service';
import { CLIENT_REQUEST_ID_HEADER, httpSpanRegistry } from './http-span-registry';
import { isOwnedApiUrl } from './owned-api';
import {
  canonicalizeDashboardRoute,
  canonicalizeOwnedApiRoute,
  normalizePath,
} from './route-pattern';
import { registerOwnedApiError } from './owned-api-error-registry';
import type {
  DiagnosticContext,
  OwnedApiErrorReport,
  TransportFailureReason,
} from './owned-api-error-registry';

@Injectable()
export class RequestObservabilityInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);
  private readonly errorReporter = inject(ErrorReporterService);
  private readonly locale = inject(LOCALE_ID);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!isOwnedApiUrl(request.url)) {
      return next.handle(request);
    }

    const clientRequestId = request.headers.get(CLIENT_REQUEST_ID_HEADER) ?? globalThis.crypto.randomUUID();
    const correlatedRequest = request.clone({
      headers: request.headers.set(CLIENT_REQUEST_ID_HEADER, clientRequestId),
    });

    return defer(() => {
      const startedAt = performance.now();
      const url = new URL(correlatedRequest.urlWithParams, window.location.origin);
      const apiRoute = canonicalizeOwnedApiRoute(url.pathname);
      const apiTarget = getApiTarget(url.hostname);
      const dashboardRoute = getDashboardRoute(this.router);
      httpSpanRegistry.beginOwnedRequest(clientRequestId, {
        attributes: {
          'client.request_id': clientRequestId,
          'dashboard.route': dashboardRoute,
          'kleksi.api.route': apiRoute,
          'kleksi.api.target': apiTarget,
        },
        name: `${correlatedRequest.method.toUpperCase()} ${apiRoute}`,
      });

      return next.handle(correlatedRequest).pipe(
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse) {
            const report = this.buildFailureReport(
              correlatedRequest,
              error,
              clientRequestId,
              apiRoute,
              apiTarget,
              dashboardRoute,
              startedAt,
            );
            registerOwnedApiError(error, report);

            if (shouldAutoReportHttpError(error)) {
              this.captureFailure(error, report);
            }
          }

          return throwError(() => error);
        }),
        finalize(() => httpSpanRegistry.complete(clientRequestId)),
      );
    });
  }

  private buildFailureReport(
    request: HttpRequest<unknown>,
    error: HttpErrorResponse,
    clientRequestId: string,
    apiRoute: string,
    apiTarget: string,
    dashboardRoute: string,
    startedAt: number,
  ): OwnedApiErrorReport {
    const url = new URL(request.urlWithParams, window.location.origin);
    const applicationId = url.pathname.match(/\/apps\/([^/]+)/)?.[1];
    const apiGatewayRequestId =
      error.headers.get('x-amzn-requestid') ??
      error.headers.get('apigw-requestid') ??
      error.headers.get('x-amz-apigw-id');
    const online = navigator.onLine;
    const baseReport = {
      apiGatewayRequestId: apiGatewayRequestId?.trim() || undefined,
      apiTarget,
      apiUrl: `${url.origin}${normalizePath(url.pathname)}`,
      applicationId,
      clientRequestId,
      dashboardRoute,
      durationMs: Math.round(performance.now() - startedAt),
      locale: $localize.locale ?? this.locale,
      method: request.method.toUpperCase(),
      online,
      route: apiRoute,
      ...httpSpanRegistry.getCorrelation(clientRequestId),
    };

    if (error.status === 0) {
      return {
        ...baseReport,
        diagnostics: getSafeTransportDiagnostics(error.error),
        kind: 'transport',
        reason: classifyTransportFailure(error.error, online),
      };
    }

    return {
      ...baseReport,
      diagnostics: getSafeResponseDiagnostics(error),
      kind: 'http-response',
      status: error.status,
    };
  }

  private captureFailure(error: HttpErrorResponse, report: OwnedApiErrorReport): void {
    if (!httpSpanRegistry.markErrorCaptured(report.clientRequestId)) {
      return;
    }

    this.errorReporter.captureOwnedApiFailure(error, report);
  }
}

function getDashboardRoute(router: Router): string {
  const configuredRoute = getConfiguredRoute(router.routerState.snapshot.root);
  return canonicalizeDashboardRoute(configuredRoute ?? router.url);
}

function getConfiguredRoute(root: ActivatedRouteSnapshot): string | undefined {
  const routeSegments: string[] = [];
  let current: ActivatedRouteSnapshot | undefined = root.firstChild ?? undefined;

  while (current) {
    const configuredPath = current.routeConfig?.path;
    if (configuredPath && configuredPath !== '**') {
      routeSegments.push(configuredPath);
    }
    current = current.firstChild ?? undefined;
  }

  return routeSegments.length > 0 ? `/${routeSegments.join('/')}` : undefined;
}

function getSafeResponseDiagnostics(error: HttpErrorResponse): DiagnosticContext {
  return {
    cloudfront_pop: readHeader(error, 'x-amz-cf-pop'),
    cloudfront_request_id: readHeader(error, 'x-amz-cf-id'),
    cloudfront_result: readHeader(error, 'x-cache'),
  };
}

function getSafeTransportDiagnostics(underlyingError: unknown): DiagnosticContext {
  return {
    progress_event_type: getProgressEventType(underlyingError),
    transport_error_name: getSafeErrorName(underlyingError),
    transport_error_type: getSafeErrorType(underlyingError),
  };
}

function classifyTransportFailure(underlyingError: unknown, online: boolean): TransportFailureReason {
  const eventType = getProgressEventType(underlyingError)?.toLowerCase();
  const errorName = getSafeErrorName(underlyingError)?.toLowerCase();

  if (eventType === 'abort' || errorName === 'aborterror') {
    return 'aborted';
  }
  if (eventType === 'timeout' || errorName === 'timeouterror') {
    return 'timeout';
  }
  if (!online) {
    return 'offline';
  }
  return 'network-or-cors';
}

function getProgressEventType(value: unknown): string | undefined {
  if (typeof ProgressEvent === 'undefined' || !(value instanceof ProgressEvent)) {
    return undefined;
  }
  return sanitizeDiagnosticToken(value.type);
}

function getSafeErrorName(value: unknown): string | undefined {
  if (!value || typeof value !== 'object' || !('name' in value)) {
    return undefined;
  }
  return sanitizeDiagnosticToken((value as { name?: unknown }).name);
}

function getSafeErrorType(value: unknown): string {
  if (value === null) {
    return 'null';
  }
  if (typeof DOMException !== 'undefined' && value instanceof DOMException) {
    return 'DOMException';
  }
  if (typeof ProgressEvent !== 'undefined' && value instanceof ProgressEvent) {
    return 'ProgressEvent';
  }
  if (typeof ErrorEvent !== 'undefined' && value instanceof ErrorEvent) {
    return 'ErrorEvent';
  }
  if (value instanceof Error) {
    return 'Error';
  }
  return typeof value === 'object' ? 'object' : typeof value;
}

function sanitizeDiagnosticToken(value: unknown): string | undefined {
  if (typeof value !== 'string' || !/^[a-z][a-z0-9_.:-]{0,63}$/i.test(value)) {
    return undefined;
  }
  return value;
}

function readHeader(error: HttpErrorResponse, name: string): string | undefined {
  return error.headers.get(name)?.trim() || undefined;
}

function getApiTarget(hostname: string): string {
  const parts = hostname.split('.');
  if (parts.length >= 2 && parts[1] === 'api') {
    return `${parts[0]}-api`;
  }

  return parts[0] ?? hostname;
}
